import TotalMetric from '@/Enums/ReportingDashboard/TotalMetric.enum';
import TotalSlide from '@/components/TotalSlide';
import { useDownloadOfflineSalesConversions } from '@/hooks/useDownloadOfflineSalesConversions';
import { useAppSelector } from '@/store/hooks';
import { selectOfflineSalesData } from '@/store/selectors/attribution/offlineSales.selectors';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import TotalsCardHeader from './TotalsCardHeader';

const ImpressionsTooltip = 'The number of exposures during the month of analysis and the preceding month.';

const ReachTooltip = 'The number of unique households (HHs) exposed to the campaign.';

const TotalRecordsUploadedTooltip =
  'The comprehensive set of records supplied by the advertiser for analysis. These may include calls, new customer sales, qualified leads, or other metrics.';

const TotalMatchedRecordsTooltip =
  'A subset of the total uploaded records that were successfully correlated using our data clean room identity solution.';

const TotalConversionsTooltip =
  'A subset of the total matched records that experienced media exposure during the month of analysis and the prior month.';

const ExtrapolatedConversionsTooltip =
  'As the conversions represent a subset or sample of the Total Matched Records, this extrapolation provides a more precise projection of the potential campaign impact.';

const MediaConversionsPercentTooltip = 'The ratio of extrapolated conversions divided by the Total Records Uploaded.';

const UniqueCVRTooltip =
  'The total number of extrapolated conversions divided by the total number of Unique Households Reached.';

const TotalsCard = () => {
  const { data, loading } = useAppSelector(selectOfflineSalesData);
  const { download, isDownloading } = useDownloadOfflineSalesConversions();

  const downloadDisabled = loading || isDownloading || !data.matchedConversions;

  const matchedConversionsLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
      <TotalsCardHeader label='Total Conversions' tooltipValue={TotalConversionsTooltip} />
      <Tooltip title='Download converted customers as CSV'>
        <span>
          <IconButton size='small' disabled={downloadDisabled} onClick={download} aria-label='Download conversions CSV'>
            {isDownloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize='small' />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '30px',
        marginTop: '30px',
      }}
    >
      <TotalSlide
        metric={TotalMetric.Impressions}
        value={data.impressions}
        label={<TotalsCardHeader label='Impressions' tooltipValue={ImpressionsTooltip} />}
        loading={loading}
      />
      <TotalSlide
        metric={TotalMetric.Reach}
        value={data.reach}
        label={<TotalsCardHeader label='Reach' tooltipValue={ReachTooltip} />}
        loading={loading}
      />
      <TotalSlide
        metric={TotalMetric.TotalSales}
        value={data.totalSales}
        label={<TotalsCardHeader label='Total Records Uploaded' tooltipValue={TotalRecordsUploadedTooltip} />}
        loading={loading}
      />
      <TotalSlide
        metric={TotalMetric.MatchedSales}
        value={data.matchedSales}
        label={<TotalsCardHeader label='Total Matched Records' tooltipValue={TotalMatchedRecordsTooltip} />}
        loading={loading}
      />
      <TotalSlide
        metric={TotalMetric.MatchedConversions}
        value={data.matchedConversions}
        label={matchedConversionsLabel}
        loading={loading}
      />
      <TotalSlide
        metric={TotalMetric.ExtrapolatedConversions}
        value={data.extrapolatedConversions}
        label={<TotalsCardHeader label='Extrapolated Conversions' tooltipValue={ExtrapolatedConversionsTooltip} />}
        loading={loading}
      />
      <TotalSlide
        metric={TotalMetric.MediaSalesPercent}
        value={data.mediaSalesPercent}
        label={<TotalsCardHeader label='% of Conversions from Media' tooltipValue={MediaConversionsPercentTooltip} />}
        loading={loading}
      />
      <TotalSlide
        metric={TotalMetric.UniqueCVR}
        value={data.uniqueCVR}
        label={<TotalsCardHeader label='Unique Conversion Rate (CVR)' tooltipValue={UniqueCVRTooltip} />}
        loading={loading}
      />
    </Box>
  );
};

export default TotalsCard;
